import type { EvidenceAnswerBaseProps } from '../../data/analytics/answer';
/**
 * `EvidenceAnswerPorts` — the ONE bundle the answer surface crosses on
 * (ruling 62), and the provider/hook that deliver it.
 *
 * NINETEEN members: the original four, plus route, authentication, OCR,
 * partner, source-action, answer-event, CPD, print, figure and navigation
 * capabilities, and the four the answer text reads (region hook, the two
 * markdown maps, message context). `response` is a twentieth, optional member:
 * only a host that renders `TextResponse` binds it, and the component throws
 * naming that port when the member is absent. Bound once per composition point
 * that renders an answer surface; a component reading a port with no provider
 * above it throws and names the port rather than rendering a degraded badge.
 *
 * THE FAVICON RESOLVER IS NOT ONE OF THEM (amendment 62c). Ruling 63 put
 * `getFaviconUrl` on `@heidi/utils`, a door this zone may import, so a port
 * bought nothing — and it cost: it was `PartnerLogo`'s ONLY member, which made
 * a logo in the settings tree, the mobile source drawer and the clinical-tools
 * submenu throw for want of a provider none of those paths has.
 *
 * IT LIVES IN `/ui`, NOT `/contracts`, and that is not a placement accident:
 * `ocrQualityTag` and `sourceActionsComponent` are `ComponentType`, and
 * `manifest-boundary.test.ts`'s contracts-inertness case refuses `react` by
 * name. Every react-free payload and vocabulary type named below is declared on
 * `/contracts` instead, which is the split ruling 24 ratifies.
 */

import type {
  MarkdownComponents,
  ProseDensity,
  ResponseProseComponents,
} from '@heidi/answer-kit';
import type { Region } from '@heidi/schema/ml-scribe';
import {
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type Context,
  type ReactElement,
  type ReactNode,
} from 'react';
import type {
  ChatMode,
  EvidenceSourceInteraction,
  EvidenceSourceSurface,
  HtmlEmbedHint,
  IntegrationPartnerConfig,
  SessionConversation,
  SimpleCitation,
  SourceCardActionsProps,
  ViewerSource,
} from '../../contracts';

/** Identity the markdown link renderer reads while an answer is on screen. */
export interface EvidenceMessageContextValue {
  chatId?: string;
  messageId?: string;
  sessionId?: string;
  defaultSurface?: EvidenceSourceSurface;
  defaultInteraction?: EvidenceSourceInteraction;
}

/** Region lookup `TextResponse` passes to the key-safety-notes mute rule. */
export interface KindeRegionState {
  region: Region | undefined;
  isPending: boolean;
  isError: boolean;
}

/** The six arguments `TextResponse` has always handed the actions hook. */
export type UseResponseActions = (
  responseText: string,
  mode?: string,
  sessionId?: string | null,
  chatTitle?: string,
  chatId?: string | null,
  messageId?: string | null
) => {
  onAddToNote: () => void;
  onReplaceNote: () => void;
  onAddNewTab: () => void;
};

/**
 * Chat-owned chrome `TextResponse` renders. Optional on the bundle: a host
 * that does not render an answer body leaves it unbound.
 */
export interface EvidenceResponsePorts {
  useResponseActions: UseResponseActions;
  ActionsMenu: ComponentType<{
    onAddToNote?: () => void;
    onReplaceNote?: () => void;
    onCreateNewTab?: () => void;
    isSessionless?: boolean;
  }>;
  UserNotes: ComponentType<{
    rawContent?: string;
    sessionId?: string | null;
    mode?: ChatMode;
    chatTitle?: string;
    chatId?: string | null;
    messageId?: string | null;
  }>;
  HeidiButton: ComponentType<{
    href?: string;
    children?: ReactNode;
    sessionId?: string | null;
    chatId?: string;
    messageId?: string;
  }>;
  CustomerFeedback: ComponentType<{
    conversation?: SessionConversation;
    position?: number;
    mode?: string;
  }>;
}

export interface EvidenceAnswerPorts {
  /** 1 — open a source in the in-app viewer panel. `useSetAtom(viewingSourceAtom)`. */
  openInViewer: (source: ViewerSource) => void;
  /** 2 — open a library document in the in-app preview. `useSetAtom(documentPreviewAtom)`. */
  openLibraryDocument: (documentId: string) => void;
  /** 3 — open a URL in a new browser tab. `window.open`. */
  openInNewTab: (url: string) => void;
  /**
   * 5 — is the current route public? A THUNK, not a boolean: the bundle is
   * memoised at the composition point and the route changes under it.
   */
  isPublicRoute: () => boolean;
  /**
   * 6 — does this URL need an authenticated fetch (or inline HTML render)?
   * `isAuthenticatedSourceUrl`, which reads nine `NEXT_PUBLIC_*` values through
   * `getDomains()` and stays app-side by ruling 52.
   */
  isAuthenticatedUrl: (url: string, embed?: HtmlEmbedHint) => boolean;
  /**
   * 7 — the OCR-quality tag for a cited library document. ONE component slot
   * rather than a resolver plus a badge: `useCitationOcrQuality` is a HOOK
   * reading a feature flag, and a resolver port would have package render call
   * an app hook.
   */
  ocrQualityTag: ComponentType<{ citation: SimpleCitation }>;
  /** 8 — partner branding by id. Reads the query cache during render. */
  getPartnerConfig: (partnerId: string) => IntegrationPartnerConfig | undefined;
  /** 9 — the source action row and its feedback subgraph, bound by the host. */
  sourceActionsComponent: ComponentType<SourceCardActionsProps>;
  /** App-resolved context shared by answer analytics. */
  analyticsBaseProps: EvidenceAnswerBaseProps;
  /** 11 — mark a CPD source read by URL. CPD is uncarved, so it crosses. */
  markSourceRead: (url: string, title?: string) => void;
  /**
   * 12 — reprint a generated document. `triggerBrowserPrint` fetches the URL,
   * loads it into a hidden same-origin iframe and opens the native dialog; the
   * fetch and the allow-list it checks are app-side, so the port is one-way and
   * the block never sees the promise.
   */
  printDocument: (url: string, context?: { toolId?: string }) => void;
  /**
   * 13 — exchange an opaque partner-figure id for a short-lived authenticated
   * object URL. Storage coordinates never cross this port.
   */
  resolvePartnerFigureUrl: (figureId: string) => Promise<string>;
  /** 14 — whether CPD queries may run on this surface. */
  isCpdQueryEnabled: boolean;
  /** 15 — navigate to an app route in response to a surface action. */
  onNavigate: (href: string) => void;
  /** 16 — Kinde region, read as a hook so a flip during render is visible. */
  useKindeRegion: () => KindeRegionState;
  /** 17 — Evidence prose map for the column density this answer renders at. */
  getEvidenceMdComponents: (density: ProseDensity) => ResponseProseComponents;
  /** 18 — legacy markdown map, for an answer that is not Evidence mode. */
  legacyMdComponents: MarkdownComponents;
  /** 19 — chat and message identity for inline source clicks. */
  messageContext: Context<EvidenceMessageContextValue>;
  /**
   * 20 — actions, notes, buttons and feedback. Unbound hosts throw from
   * `TextResponse` rather than rendering a body with no actions.
   */
  response?: EvidenceResponsePorts;
}

const EvidenceAnswerPortsContext = createContext<EvidenceAnswerPorts | null>(null);

export interface EvidenceAnswerPortsProviderProps {
  ports: EvidenceAnswerPorts;
  children: ReactNode;
}

/**
 * Mount once per composition point that renders an answer surface. Memoise
 * `ports` at the call site — every consumer re-renders when the identity
 * changes, which is why member 6 is a thunk rather than a resolved boolean.
 */
export const EvidenceAnswerPortsProvider = ({
  ports,
  children,
}: EvidenceAnswerPortsProviderProps): ReactElement =>
  createElement(EvidenceAnswerPortsContext.Provider, { value: ports }, children);

/**
 * Read the bundle. Throws when no provider is above the caller, naming the
 * missing composition point — a degraded render would ship a badge with no
 * analytics and no viewer routing, and look correct.
 */
export const useEvidenceAnswerPorts = (): EvidenceAnswerPorts => {
  const ports = useContext(EvidenceAnswerPortsContext);
  if (!ports) {
    throw new Error(
      'EvidenceAnswerPorts is missing: wrap this answer surface in <EvidenceAnswerPortsProvider>.'
    );
  }
  return ports;
};
